import React, { useState, useEffect } from 'react';
import { db } from '../services/FishingDB';
import { FishingArea } from '../entities/FishingEntities';
import { useLanguage } from '../contexts/LanguageContext';
import AreaDetailModal from '../components/AreaDetailModal';
import Toast from '../components/Toast';
import '../App.css';
import '../styles/Pages.css';

export const AreasPage: React.FC = () => {
  const [areas, setAreas] = useState<FishingArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState<FishingArea | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'info' | 'success' | 'error'>('info');
  const { t } = useLanguage();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await db.areas.toArray();
      setAreas(data);
    } catch (error) {
      console.error('Failed to load areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleAreaClick = (area: FishingArea) => {
    setSelectedArea(area);
    setShowDetailModal(true);
  };

  const handleAreaUpdated = () => {
    loadData(); // Reload all data
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-white/70">
        <p>{t.loadingAreas}</p>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
            <div>
              <h1 style={{ color: 'white' }}>🗺️ {t.areas}</h1>
              <p className="subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {areas.length} {areas.length === 1 ? t.place : t.places} {t.areasVisited}
              </p>
            </div>
          </div>

        {areas.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🗺️</div>
            <h2>{t.noAreasYet}</h2>
            <p>{t.noAreasDescription}</p>
          </div>
        ) : (
          <div className="areas-container">
            {areas.map(area => (
              <div 
                key={area.id} 
                className="area-card"
                onClick={() => handleAreaClick(area)}
                style={{ cursor: 'pointer' }}
              >
                <div className="area-card-header">
                  <div className="area-title">
                    <h3>📍 {area.name}</h3>
                    {area.totalCatches > 0 && (
                      <span className="area-badge">{area.totalCatches} {t.catches}</span>
                    )}
                  </div>
                </div>

                <div className="area-info">
                  <div className="info-row">
                    <span className="info-label">{t.coordinates}</span>
                    <span className="area-coords">
                      {area.centerLat.toFixed(4)}, {area.centerLng.toFixed(4)}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="info-label">{t.radius}</span>
                    <span className="info-value">{area.radius}m</span>
                  </div>

                  <div className="info-row">
                    <span className="info-label">{t.lastVisit}</span>
                    <span className="info-value">
                      {new Date(area.lastVisited).toLocaleDateString('sv-SE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>

                <div className="area-stats">
                  <div className="stat-item">
                    <span className="stat-icon">🎣</span>
                    <div className="stat-details">
                      <span className="stat-value">{area.totalCatches}</span>
                      <span className="stat-label">{t.catches}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Area Detail Modal */}
      <AreaDetailModal
        area={selectedArea}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedArea(null);
        }}
        onUpdate={handleAreaUpdated}
        onSuccess={showToast}
      />

      {/* Toast notifications */}
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} />
      )}
    </>
  );
};
